import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import PageHeader from '../common/PageHeader';
import LoadingState from '../common/LoadingState';
import { WINDOWS, fmtAgo, errMsg, cachedGet } from './shared';
import './activity.css';

const Stat = ({ icon, label, value, hint }) => (
    <div className="ex-stat">
        <span className="ex-stat-icon"><i className={`pi ${icon}`} /></span>
        <div className="ex-stat-body">
            <div className="ex-stat-label">{label}</div>
            <div className="ex-stat-value">{value}</div>
            {hint && <div className="ex-stat-hint">{hint}</div>}
        </div>
    </div>
);

// Platform Activity overview: every platform with deliveries in the window,
// problems first. Click a platform to see what it sent and why loans were
// or weren't bid.
const Activity = () => {
    const navigate = useNavigate();
    const [days, setDays] = useState(7);
    const [overview, setOverview] = useState(null);
    const [error, setError] = useState(null);

    const load = (force = false) => {
        setOverview(null);
        setError(null);
        cachedGet('/activity/overview', { params: { days }, force })
            .then((res) => setOverview(res.data))
            .catch((err) => setError(errMsg(err)));
    };

    useEffect(() => load(), [days]); // eslint-disable-line react-hooks/exhaustive-deps

    const t = overview?.totals;
    const open = (p) => navigate(`/activity/${encodeURIComponent(p.key)}`);

    return (
        <div className="content-container">
            <PageHeader
                title="Originator Activity"
                subtitle="Pick an Originator to see what we received, why a file failed, or why a loan was or wasn't bid."
                icon="pi-history"
                actions={<>
                    <Dropdown value={days} options={WINDOWS} onChange={(e) => setDays(e.value)} />
                    <Button icon="pi pi-refresh" className="p-button-text" onClick={() => load(true)} tooltip="Refresh" />
                </>}
            />
            {error && <div className="ex-inline-error">{error}</div>}
            {!overview && !error && <LoadingState label="Loading activity…" />}
            {overview && (
                <>
                    <div className="ex-stat-grid">
                        <Stat icon="pi-inbox" label="Deliveries" value={t.total.toLocaleString('en-US')} hint={`${overview.platforms.length} Originators`} />
                        <Stat icon="pi-check-circle" label="Succeeded" value={(t.success + t.mapped).toLocaleString('en-US')} />
                        <Stat icon="pi-times-circle" label="Failed" value={t.failed.toLocaleString('en-US')} />
                        <Stat icon="pi-clock" label="Stuck / in progress" value={`${t.stuck} / ${t.running}`} hint="Stuck = no result after 3 h" />
                    </div>

                    <section className="ex-section">
                        <div className="ex-section-header">
                            <div>
                                <div className="ex-section-title">Originators</div>
                                <div className="ex-section-subtitle">Originators with failed or stuck deliveries are listed first. Click a row for details.</div>
                            </div>
                        </div>
                        <DataTable value={overview.platforms} dataKey="key" size="small" responsiveLayout="scroll"
                            selectionMode="single" onRowSelect={(e) => open(e.data)} rowClassName={() => 'act-clickable'}>
                            <Column field="name" header="Originator" body={(p) => <strong>{p.name}</strong>} sortable />
                            <Column field="total" header="Deliveries" sortable />
                            <Column header="Succeeded" body={(p) => p.success + p.mapped} />
                            <Column field="failed" header="Failed" sortable body={(p) => (p.failed ? <span className="ex-status ex-status-danger">{p.failed}</span> : 0)} />
                            <Column field="stuck" header="Stuck" sortable body={(p) => (p.stuck ? <span className="ex-status ex-status-warning">{p.stuck}</span> : 0)} />
                            <Column field="last_received_at" header="Last delivery" sortable body={(p) => fmtAgo(p.last_received_at)} />
                            <Column header="Latest failure" body={(p) => (p.last_failure
                                ? <span>{p.last_failure.headline} <small className="text-color-secondary">· {fmtAgo(p.last_failure.at)}</small></span>
                                : '—')} />
                        </DataTable>
                    </section>
                </>
            )}

        </div>
    );
};

export default Activity;
